export type IdentityCardFormat = 'placard' | 'windshield' | 'story';

export interface IdentityCardInput {
  vehicleLabel: string;
  ownerLabel: string;
  profileUrl: string;
  qrImageUrl: string;
}

const FORMATS: Record<
  IdentityCardFormat,
  { width: number; height: number; label: string }
> = {
  placard: { width: 1200, height: 1600, label: 'show-placard' },
  windshield: { width: 1400, height: 600, label: 'windshield-card' },
  story: { width: 1080, height: 1920, label: 'social-story' },
};

const COLORS = {
  bg: '#09090b',
  surface: '#18181b',
  border: 'rgba(255,255,255,0.12)',
  text: '#fafafa',
  muted: '#a1a1aa',
  dim: '#71717a',
  accent: '#d4d4d8',
};

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load QR image'));
    img.src = src;
  });
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number
): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
      if (lines.length >= maxLines) break;
    } else {
      current = test;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  return lines;
}

const TRIM_INSET = 48;
const CROP_LEN = 32;

function drawCropMarks(ctx: CanvasRenderingContext2D, width: number, height: number) {
  const t = TRIM_INSET;
  const len = CROP_LEN;
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 2;

  const corners: Array<[number, number, number, number, number, number]> = [
    [t, t, t + len, t, t, t + len],
    [width - t, t, width - t - len, t, width - t, t + len],
    [t, height - t, t + len, height - t, t, height - t - len],
    [width - t, height - t, width - t - len, height - t, width - t, height - t - len],
  ];

  for (const [x1, y1, x2, y2, x3, y3] of corners) {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.moveTo(x1, y1);
    ctx.lineTo(x3, y3);
    ctx.stroke();
  }

  ctx.setLineDash([8, 8]);
  ctx.strokeStyle = 'rgba(255,255,255,0.12)';
  ctx.lineWidth = 1;
  ctx.strokeRect(t, t, width - t * 2, height - t * 2);
  ctx.restore();
}

function drawBrandMark(ctx: CanvasRenderingContext2D, x: number, y: number, size: number) {
  ctx.save();
  ctx.fillStyle = COLORS.text;
  ctx.font = `600 ${size}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'left';
  ctx.fillText('BILT', x, y);
  ctx.fillStyle = COLORS.dim;
  ctx.font = `500 ${Math.round(size * 0.35)}px system-ui, -apple-system, sans-serif`;
  ctx.fillText('VEHICLE IDENTITY', x, y + size * 0.55);
  ctx.restore();
}

function drawQrFrame(
  ctx: CanvasRenderingContext2D,
  qr: HTMLImageElement,
  x: number,
  y: number,
  size: number
) {
  const pad = 16;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x - pad, y - pad, size + pad * 2, size + pad * 2);
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 2;
  ctx.strokeRect(x - pad, y - pad, size + pad * 2, size + pad * 2);
  ctx.drawImage(qr, x, y, size, size);
}

function drawCard(
  ctx: CanvasRenderingContext2D,
  format: IdentityCardFormat,
  input: IdentityCardInput,
  qr: HTMLImageElement
) {
  const { width, height } = FORMATS[format];
  const shortUrl = input.profileUrl.replace(/^https?:\/\//, '');

  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 3;
  ctx.strokeRect(TRIM_INSET, TRIM_INSET, width - TRIM_INSET * 2, height - TRIM_INSET * 2);

  if (format === 'windshield') {
    const qrSize = 280;
    const leftPad = 80;
    const qrX = width - qrSize - 100;
    const qrY = (height - qrSize) / 2;

    drawBrandMark(ctx, leftPad, 100, 36);

    ctx.textAlign = 'left';
    ctx.fillStyle = COLORS.text;
    ctx.font = 'bold 52px system-ui, -apple-system, sans-serif';
    const titleLines = wrapText(ctx, input.vehicleLabel, width - qrSize - 220, 2);
    titleLines.forEach((line, i) => {
      ctx.fillText(line, leftPad, 200 + i * 62);
    });

    ctx.fillStyle = COLORS.muted;
    ctx.font = '28px system-ui, -apple-system, sans-serif';
    ctx.fillText(`Builder · ${input.ownerLabel}`, leftPad, 340);

    ctx.fillStyle = COLORS.dim;
    ctx.font = '22px system-ui, -apple-system, sans-serif';
    ctx.fillText('Scan for full build profile', leftPad, height - 80);
    ctx.fillStyle = COLORS.accent;
    ctx.font = '20px system-ui, -apple-system, sans-serif';
    ctx.fillText(shortUrl, leftPad, height - 48);

    drawQrFrame(ctx, qr, qrX, qrY, qrSize);
    drawCropMarks(ctx, width, height);
    return;
  }

  const isStory = format === 'story';
  const margin = isStory ? 72 : 64;
  const qrSize = isStory ? 440 : 380;
  const titleSize = isStory ? 58 : 48;
  const ownerSize = isStory ? 32 : 28;

  drawBrandMark(ctx, margin, margin + 20, isStory ? 40 : 34);

  ctx.textAlign = 'center';
  ctx.fillStyle = COLORS.text;
  ctx.font = `bold ${titleSize}px system-ui, -apple-system, sans-serif`;
  const titleY = isStory ? 280 : 220;
  const titleLines = wrapText(ctx, input.vehicleLabel, width - margin * 2, isStory ? 3 : 2);
  titleLines.forEach((line, i) => {
    ctx.fillText(line, width / 2, titleY + i * (titleSize + 12));
  });

  const ownerY = titleY + titleLines.length * (titleSize + 12) + (isStory ? 36 : 28);
  ctx.font = `${ownerSize}px system-ui, -apple-system, sans-serif`;
  ctx.fillStyle = COLORS.muted;
  ctx.fillText(`Builder · ${input.ownerLabel}`, width / 2, ownerY);

  const qrY = ownerY + (isStory ? 80 : 64);
  drawQrFrame(ctx, qr, (width - qrSize) / 2, qrY, qrSize);

  const footerY = qrY + qrSize + (isStory ? 72 : 56);
  ctx.fillStyle = COLORS.dim;
  ctx.font = `${isStory ? 28 : 24}px system-ui, -apple-system, sans-serif`;
  ctx.fillText('Scan for full build profile', width / 2, footerY);

  ctx.fillStyle = COLORS.accent;
  ctx.font = `${isStory ? 24 : 20}px system-ui, -apple-system, sans-serif`;
  ctx.fillText(shortUrl, width / 2, footerY + (isStory ? 40 : 32));

  ctx.strokeStyle = COLORS.border;
  ctx.beginPath();
  ctx.moveTo(margin, height - margin - 48);
  ctx.lineTo(width - margin, height - margin - 48);
  ctx.stroke();

  ctx.fillStyle = COLORS.dim;
  ctx.font = `500 ${isStory ? 22 : 18}px system-ui, -apple-system, sans-serif`;
  ctx.fillText('biltspec.com', width / 2, height - margin - 12);
  drawCropMarks(ctx, width, height);
}

export async function downloadIdentityCard(format: IdentityCardFormat, input: IdentityCardInput): Promise<void> {
  const spec = FORMATS[format];
  const canvas = document.createElement('canvas');
  canvas.width = spec.width;
  canvas.height = spec.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  const qr = await loadImage(input.qrImageUrl);
  drawCard(ctx, format, input, qr);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Failed to export card');

  const slug = input.vehicleLabel.replace(/\s+/g, '-').toLowerCase().slice(0, 40);
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `bilt-${spec.label}-${slug}.png`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}
